/**
 * ENG-5966: shared timezone-resolution rule for scheduled tasks.
 *
 * The write-side counterpart to `resolveEffectiveTimezone` in
 * `prompt-wrapper.ts` (which is read-side, deciding whether to render the
 * [NOW] block). This is the single source of truth for "what IANA timezone
 * should a scheduled-task row be PERSISTED with", consumed by both the
 * host-runtime API endpoints and the webapp agent create/update routes so the
 * inheritance rule can't drift between them.
 */

/**
 * True when a timezone value carries no explicit IANA zone and therefore means
 * "inherit the team default, then fall back to UTC". Covers:
 *  - `undefined` / `null`
 *  - blank / whitespace-only strings
 *  - the literal sentinel `'auto'` — scheduled-task templates ship this
 *    (e.g. the agent-role library's "Hourly Urgent Email Check"); a webapp
 *    path even persisted it verbatim via `task.timezone ?? 'UTC'`
 *  - `'UTC'` itself — the model's clock already reads UTC, so for
 *    date-anchoring it's indistinguishable from "unset"
 */
export function isUnsetTimezone(tz: string | null | undefined): boolean {
  if (!tz) return true;
  const trimmed = tz.trim();
  return (
    trimmed.length === 0 ||
    trimmed.toLowerCase() === 'auto' ||
    trimmed.toUpperCase() === 'UTC'
  );
}

/**
 * Resolve the IANA timezone to PERSIST for a scheduled task.
 *
 * When `requested` is unset (blank / `'auto'` / `'UTC'`), inherit
 * `teamTimezone`; when the team has no usable tz either, fall back to `'UTC'`.
 * An explicit non-UTC `requested` always wins over the team setting.
 *
 * Both arguments are normalized identically, so passing a pre-validated team
 * tz (e.g. the API's `getTeamTimezone()`, which returns `null` for blank/UTC)
 * or a raw `team.settings.timezone` string both behave correctly.
 */
export function resolveScheduledTaskTimezone(
  requested: string | null | undefined,
  teamTimezone: string | null | undefined,
): string {
  if (!isUnsetTimezone(requested)) return requested!.trim();
  if (!isUnsetTimezone(teamTimezone)) return teamTimezone!.trim();
  return 'UTC';
}

/**
 * ENG-6695: resolve the IANA timezone an AGENT should be scheduled / restarted
 * in. Precedence (highest first): the agent's own `timezone` override → the
 * team default (`teams.settings->>'timezone'`) → the org default
 * (`organizations.settings->>'timezone'`) → `'UTC'`.
 *
 * Every tier is normalized via {@link isUnsetTimezone}, so blank / `'auto'` /
 * `'UTC'` at any level transparently falls through to the next — and a null
 * agent timezone reproduces today's team/org-inherited behaviour exactly
 * (backward-compatible).
 *
 * The manager points its disruptive-restart / maintenance-window computation at
 * this so "off-peak" is calculated in the operator's zone, not the org default.
 * (A future `reports_to`-person timezone, once organization_people grows one,
 * slots in as the highest-precedence source ahead of the agent override.)
 */
export function resolveAgentTimezone(
  agentTimezone: string | null | undefined,
  teamTimezone: string | null | undefined,
  orgTimezone: string | null | undefined,
  agentTimezoneExpiresAt?: string | Date | null,
): string {
  if (
    !isUnsetTimezone(agentTimezone)
    && !isAgentTimezoneOverrideExpired(agentTimezoneExpiresAt)
  ) {
    return agentTimezone!.trim();
  }
  if (!isUnsetTimezone(teamTimezone)) return teamTimezone!.trim();
  if (!isUnsetTimezone(orgTimezone)) return orgTimezone!.trim();
  return 'UTC';
}

/**
 * ENG-8016: has a time-boxed agent timezone override lapsed?
 *
 * An agent can pin its own zone with a TTL - "I'm in Glasgow until Friday" -
 * and the override must revert on its own, because the whole point is that
 * nobody has to remember to undo it.
 *
 * The lapse is enforced HERE, at resolution time, rather than by a sweeper that
 * nulls the column. That is the same shape as the email-guardrail exemption
 * expiry (ENG-7837 / ADR-0048), where the resolver treats an expired stage as
 * NULL and it "heals closed" with no cron involved. Read-time enforcement has
 * no window in which the stored value and the effective behaviour disagree, and
 * it cannot be defeated by a cron tick that failed - a sweeper that dies leaves
 * an agent silently pinned to the wrong clock, which is exactly the bug the TTL
 * exists to prevent.
 *
 * Anything unparseable is treated as NOT expired: a corrupt timestamp must not
 * silently discard an override the agent deliberately set. The write path
 * validates strict ISO-8601, so an unparseable value here means data damage,
 * and the safe reading of damaged data is "leave the operator's setting alone".
 */
export function isAgentTimezoneOverrideExpired(
  expiresAt: string | Date | null | undefined,
  now: Date = new Date(),
): boolean {
  if (expiresAt === null || expiresAt === undefined) return false;
  const expiry = expiresAt instanceof Date ? expiresAt.getTime() : Date.parse(expiresAt);
  if (Number.isNaN(expiry)) return false;
  return expiry <= now.getTime();
}
